import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const DAY_NAMES = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'];

const fmtSlot = (s) => {
  if (!s) return '-';
  const day = DAY_NAMES[s.day] || `Giorno ${s.day}`;
  return `${day} ${s.start_time}-${s.end_time} (${s.duration_min}′)`;
};

export const ComparisonModal = ({ open, onOpenChange, teacherId, teacherName, currentYear, availableYears }) => {
  const [sourceYear, setSourceYear] = useState(currentYear - 1);
  const [targetYear, setTargetYear] = useState(currentYear);
  const [tab, setTab] = useState('only_source');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && teacherId) {
      setSourceYear((prev) => Math.min(prev, currentYear - 1) || (currentYear - 1));
      setTargetYear(currentYear);
    }
  }, [open, teacherId, currentYear]);

  const load = async () => {
    if (!teacherId || !sourceYear || !targetYear || sourceYear === targetYear) return;
    setLoading(true);
    try {
      const r = await api.compareTeacherStudentsYears(teacherId, sourceYear, targetYear);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento confronto');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, teacherId, sourceYear, targetYear]);

  const groups = data ? {
    only_source: { label: `Manca in ${targetYear}/${String(targetYear + 1).slice(-2)}`, list: data.only_source, color: 'text-red-600 bg-red-50 border-red-200' },
    only_target: { label: `Nuovi in ${targetYear}/${String(targetYear + 1).slice(-2)}`, list: data.only_target, color: 'text-green-700 bg-green-50 border-green-200' },
    in_both: { label: 'Confermati (in entrambi)', list: data.in_both, color: 'text-[#2D2A4A] bg-[#F4F4F5] border-[#E4E4E7]' },
    changed: { label: 'Orario / durata cambiati', list: data.changed, color: 'text-orange-700 bg-orange-50 border-orange-200' },
  } : null;

  const activeList = groups?.[tab]?.list || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="comparison-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold uppercase tracking-wide">Confronto Anni Accademici — {teacherName}</DialogTitle>
        </DialogHeader>

        <div className="flex items-end gap-3 pt-2 border-b border-[#E4E4E7] pb-3">
          <div className="flex-1">
            <label className="text-[10px] uppercase font-semibold text-[#71717A] tracking-wider">Anno di partenza</label>
            <Select value={String(sourceYear)} onValueChange={(v) => setSourceYear(parseInt(v))}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="cmp-source-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(availableYears || []).map(y => <SelectItem key={y} value={String(y)}>{y}/{String(y + 1).slice(-2)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-lg text-[#71717A] pb-2">→</div>
          <div className="flex-1">
            <label className="text-[10px] uppercase font-semibold text-[#71717A] tracking-wider">Anno di arrivo</label>
            <Select value={String(targetYear)} onValueChange={(v) => setTargetYear(parseInt(v))}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="cmp-target-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(availableYears || []).map(y => <SelectItem key={y} value={String(y)}>{y}/{String(y + 1).slice(-2)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} disabled={loading || sourceYear === targetYear} className="rounded-sm" data-testid="cmp-refresh">
            {loading ? 'Attendere...' : 'Aggiorna'}
          </Button>
        </div>

        {sourceYear === targetYear && (
          <div className="text-xs text-red-600 italic mt-2">Scegli due anni diversi per confrontare.</div>
        )}

        {data && groups && (
          <>
            <div className="flex gap-2 mt-3 border-b border-[#E4E4E7] pb-0 flex-wrap">
              {Object.entries(groups).map(([key, g]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide border-b-2 transition-colors ${tab === key ? 'border-[#2D2A4A] text-[#2D2A4A]' : 'border-transparent text-[#71717A] hover:text-[#2D2A4A]'}`}
                  data-testid={`cmp-tab-${key}`}
                >
                  {g.label} <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#2D2A4A] text-white text-[10px] font-bold">{g.list.length}</span>
                </button>
              ))}
            </div>

            <div className="overflow-y-auto flex-1 mt-3" data-testid={`cmp-list-${tab}`}>
              {activeList.length === 0 ? (
                <div className="text-center py-8 text-sm text-[#71717A] italic">Nessun elemento in questa categoria.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-[#F4F4F5] border-y border-[#E4E4E7]">
                    <tr>
                      <th className="text-left px-3 py-2 uppercase text-[10px] tracking-wider text-[#52525B]">Alunno</th>
                      <th className="text-left px-3 py-2 uppercase text-[10px] tracking-wider text-[#52525B]">Codice</th>
                      <th className="text-left px-3 py-2 uppercase text-[10px] tracking-wider text-[#52525B]">{sourceYear}/{String(sourceYear + 1).slice(-2)}</th>
                      <th className="text-left px-3 py-2 uppercase text-[10px] tracking-wider text-[#52525B]">{targetYear}/{String(targetYear + 1).slice(-2)}</th>
                      {tab === 'changed' && <th className="text-left px-3 py-2 uppercase text-[10px] tracking-wider text-[#52525B]">Differenze</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {activeList.map((e, i) => (
                      <tr key={e.student_id} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} data-testid={`cmp-row-${e.student_id}`}>
                        <td className="px-3 py-2 font-semibold uppercase">{e.student_name}</td>
                        <td className="px-3 py-2 text-[#71717A]">{e.codice || '-'}</td>
                        <td className={`px-3 py-2 ${e.source ? '' : 'text-[#A1A1AA] italic'}`}>{fmtSlot(e.source)}</td>
                        <td className={`px-3 py-2 ${e.target ? '' : 'text-[#A1A1AA] italic'}`}>{fmtSlot(e.target)}</td>
                        {tab === 'changed' && (
                          <td className="px-3 py-2">
                            <div className="flex gap-1 flex-wrap">
                              {e.diffs.map(d => (
                                <span key={d} className="inline-block px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-bold uppercase tracking-wide">{d}</span>
                              ))}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="text-[10px] text-[#71717A] italic mt-2 pt-2 border-t border-[#E4E4E7]">
              Legenda: <b>Manca</b> = c'era nell'anno di partenza ma non c'è in quello di arrivo · <b>Nuovi</b> = presenti solo nell'anno di arrivo · <b>Confermati</b> = in entrambi · <b>Cambiati</b> = confermati ma con giorno, orario o durata modificati.
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
